import { useEffect, useState } from 'react';
import { Settings, useSaveSettings, useSession } from '@ais-ui/websockets';
import { toast } from '@ais-ui/ui-core';
import { useLocalization } from '@ais-ui/locale-management';
import defaultSettings from './defaultSettings.json';

// Settings keys that are typically added after first login
const POST_FIRST_LOGIN_KEYS = [
  ['ribbonPlugin.settings', 'ship_color_state'],
  ['ribbonPlugin.settings', 'ship_moving_symbol'],
  ['ribbonPlugin.settings', 'ship_not_moving_symbol'],
];

// biome-ignore lint/suspicious/noExplicitAny: recursive object traversal requires flexible typing
const hasKey = (obj: Record<string, any>, keyPath: string[]): boolean => {
  try {
    let current: unknown = obj;
    for (const key of keyPath) {
      if (
        current == null ||
        typeof current !== 'object' ||
        !Object.prototype.hasOwnProperty.call(current, key)
      ) {
        return false;
      }
      current = (current as Record<string, unknown>)[key];
    }
    return true;
  } catch {
    return false;
  }
};

export const isFirstTimeLogin = (currentSettings: Settings) => {
  try {
    // Return true if any key is missing
    return POST_FIRST_LOGIN_KEYS.some(
      (keyPath) => !hasKey(currentSettings, keyPath),
    );
  } catch (error) {
    console.error('Error checking first time login settings:', error);
    return false;
  }
};

export const useSettingsChecker = (settings: Settings | null) => {
  const [hasCheckedSettings, setHasCheckedSettings] = useState(false);
  const { mutate: mutateSettings } = useSaveSettings();
  const { olSession } = useSession();
  const { messages } = useLocalization();

  useEffect(() => {
    const checkAndUpdateSettings = async () => {
      try {
        if (!settings || hasCheckedSettings) {
          return;
        }

        const isFirstTime = isFirstTimeLogin(settings);
        if (!isFirstTime) {
          setHasCheckedSettings(true);
          return;
        }

        mutateSettings(defaultSettings, {
          onSuccess: () => {
            toast({
              title: messages.genericToastTitleSuccess,
              description: messages.settingsDefaultUpdateMessage,
            });
          },
          onError: () => {
            toast({
              title: messages.genericToastTitleError,
              description: messages.settingsDefaultUpdateErrorMessage,
              variant: 'destructive',
            });
          },
        });

        setHasCheckedSettings(true);
      } catch (error) {
        console.error('Error checking settings:', error);
      }
    };

    checkAndUpdateSettings();
  }, [mutateSettings, olSession, settings, messages, hasCheckedSettings]);

  return { hasCheckedSettings };
};
